import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ProdutoPromocional } from '../../../core/models/produtos/produto-promocional';
import { Configuracao } from '../../../core/models/configuracoes/configuracao';
import { PrazoPagamento } from '../../../core/models/prazos-pagamento/prazo-pagamento';
import { Pedido } from '../../../core/models/pedidos/pedido';

/**
 * Barra de filtro + navegação de modo edição + aviso de campanhas indisponíveis
 * (pedido-adicionar-itens-modal.component.html:261-374) e lista virtualizada de
 * produtos/campanhas em formato de card compacto (html:376-646 + 385-559,
 * substituindo a antiga `<table class="adicionar_itens_tabela">`).
 *
 * Escopo deliberadamente NÃO inclui o conteúdo condicionado a `produto.expandido`
 * (html:561-1160): o painel de pricing/edição de item (647-1107) e o campo de
 * observação (1136-1144) já foram movidos para `app-pedido-itens-detalhe-painel`
 * (Task 3 — ver comentário no topo daquele componente, que documenta seu escopo real
 * como 647-1107 + 1136-1144, mais amplo que o "647-977" citado neste brief). O
 * "placement" desse painel dentro da listagem é responsabilidade da Task 5
 * ("wired up in Task 5, not here" — texto literal do brief desta task).
 *
 * Duas seções do bloco expandido (html:561-646 "mobile info" duplicado do desktop, e
 * html:1145-1160 "ações mobile" duplicadas das ações desktop já cobertas pelo painel de
 * detalhe) foram tratadas como substituídas pelo layout único/responsivo do novo
 * `app-pedido-itens-detalhe-painel`, conforme a própria Task 3 documentou
 * ("a new grouped/responsive layout is meant to replace both the old desktop table and
 * its mobile duplicate"). Uma terceira seção, "Última Compra" (html:1112-1132), NÃO foi
 * reivindicada por nenhuma das duas tasks — ver task-4-report.md para detalhes.
 *
 * Componente puramente apresentacional: nenhuma lógica de negócio própria além de
 * leituras puras/derivadas de `produto`/`configuracao`/`prazoPagamentoSelecionado`
 * (mesmo precedente estabelecido pela Task 3 para `ehPrecoTravado` e as funções de
 * formatação). Toda ação do usuário é reemitida via @Output para o componente pai.
 */
@Component({
  selector: 'app-pedido-itens-catalogo',
  standalone: false,
  templateUrl: './pedido-itens-catalogo.component.html',
  styleUrls: ['./pedido-itens-catalogo.component.scss']
})
export class PedidoItensCatalogoComponent {
  /* Categorias (fixas) — espelham as constantes do componente pai (ts:59-60). */
  @Input() campanhas = 'CAMPANHAS';
  @Input() combo = 'COMBO';

  @Input() produtosExibidos: ProdutoPromocional[] = [];
  /** Id do produto atualmente selecionado/em destaque (uso adicional do card, além de `produto.expandido`). */
  @Input() produtoSelecionadoId: number = null;

  /* Filtro (Produtos) — form real: ts:327-334 (`filtro`, `codigo`, `descricao`, `marca`, `referencia`, `status`). */
  @Input() formFiltroProduto: UntypedFormGroup = null;
  @Input() filtroProdutos: any[] = [];
  @Input() filtroStatus: any[] = [];
  @Input() somentePromocoes = false;
  @Output() promocoesAlternadas = new EventEmitter<void>();
  @Input() enviadoProduto = false;
  @Input() filtrandoProduto = false;
  @Input() produtosComErro: any[] = [];
  @Input() filtroMobileExpandido = false;

  /** Gate real de visibilidade do formulário de filtro (html:263: `produtoSelecionado == null && !isEditMode`). */
  @Input() produtoSelecionado: ProdutoPromocional = null;
  @Input() isEditMode = false;
  @Input() itemEmEdicao: ProdutoPromocional = null;

  /** Aviso "Campanhas não disponíveis para alteração de pedido" (html:348: `pedido != null && filtroProdutoSelecionado == '0'`). */
  @Input() pedido: Pedido = null;
  @Input() filtroProdutoSelecionado = '2';

  /* Contexto de cálculo necessário para o resumo de preço do card (html:517-559). */
  @Input() configuracao: Configuracao = null;
  @Input() prazoPagamentoSelecionado: PrazoPagamento = null;
  @Input() exibirPontuacao = false;
  @Input() usaPendencia = false;

  /* Virtual scroll */
  @Input() itemSize = 48;
  @Input() viewportHeight = 600;
  /** Fix round 1 (Task 5 review): gate real da listagem virtualizada — no html original
   *  (pedido-adicionar-itens-modal.component.html:375, pré-Task5) todo o
   *  `cdk-virtual-scroll-viewport` ficava dentro de `@if (filtrandoProduto ||
   *  saldoGeradoErro)`, ou seja, a lista só aparecia depois de o usuário submeter um
   *  filtro. Esse componente renderizava o viewport incondicionalmente; restaurado como
   *  Input para o pai poder repassar a mesma condição sem reintroduzir lógica de negócio
   *  aqui (o filtro em si continua sempre visível, só a lista é que é condicionada). */
  @Input() exibirLista = true;

  /** TemplateRef do modal de campanha (`#adicionarItensCampanha` no pai), necessário para os
   *  botões Adicionar/Alterar da linha de campanha (html:447/450 → `modalAdicionarItensCampanha`).
   *  Não listado no brief original ("Consumes"), adicionado pelo mesmo motivo do `modalLotes`
   *  da Task 3: sem ele, a ação de campanha ficaria sem gatilho real na nova listagem. */
  @Input() modalCampanha: TemplateRef<any> = null;

  /** Fix round 2 (Task 5 review): o `<cdk-virtual-scroll-viewport>` que estava no template
   *  do pai (com `#adicionarItensProdutos`, consultado via `@ViewChild` em ~8 pontos do pai
   *  para `checkViewportSize()`/`scrollToIndex()`/leitura de `elementRef`) foi movido para
   *  este componente na Task 5, quebrando aquele `@ViewChild` (ficava sempre `undefined`,
   *  silenciosamente — todos os call sites já eram guardados por `if (...)`). Exposto aqui
   *  como propriedade pública para o pai reconectar via
   *  `@ViewChild(PedidoItensCatalogoComponent)` + um getter, sem duplicar nenhuma lógica de
   *  scroll/resize (que continua 100% no pai, inalterada). */
  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

  @Output() filtroSubmetido = new EventEmitter<void>();
  @Output() filtroAlterado = new EventEmitter<any>();
  @Output() filtroMobileAlternado = new EventEmitter<void>();
  @Output() produtoClicado = new EventEmitter<{ id_produto: number; index: number }>();
  @Output() voltarModoEdicao = new EventEmitter<void>();
  @Output() travaRemovida = new EventEmitter<number>();
  /** Cobre tanto "Adicionar" (html:447, qtde == null) quanto "Alterar" (html:450, qtde > 0) —
   *  ambos chamam `modalAdicionarItensCampanha(content, produto.id_campanha)` no original. */
  @Output() campanhaAdicionarOuAlterar = new EventEmitter<{ content: TemplateRef<any>; id_campanha: number }>();
  @Output() campanhaExcluida = new EventEmitter<number>();

  /**
   * TrackBy estável por id de produto.
   * Corpo real verificado em pedido-adicionar-itens-modal.component.ts:824-827 — o placeholder
   * do brief ("return produto.id") estava incompleto: a versão real trata `item == null` e usa
   * `_index` como fallback, o que importa para a correção do Virtual Scroll em listas com itens
   * nulos/ainda não carregados.
   */
  trackByProdutoId(_index: number, item: any): any {
    return item != null ? item.id : _index;
  }

  /** Corpo real verificado em pedido-adicionar-itens-modal.component.ts:2497-2501 (leitura pura,
   *  mesmo precedente de duplicação estabelecido pela Task 3). */
  ehPrecoTravado(produto: ProdutoPromocional): boolean {
    if (produto == null) { return false; }
    if (produto.estado_preco != null) { return produto.estado_preco.travado === true; }
    return produto.preco_tabela_travado === true;
  }

  /** Corpo real verificado em pedido-adicionar-itens-modal.component.ts:2503-2506. Ao contrário do
   *  que o comentário da Task 3 presumiu ("pode depender de mais contexto que só produto"), esta
   *  função é, na verdade, pura — depende apenas de `produto` — por isso foi duplicada aqui em vez
   *  de recebida como @Input. */
  obterLabelPrecoTravado(produto: ProdutoPromocional): string {
    if (produto == null) { return 'Preço de tabela utilizado'; }
    return produto.preco_tabela_travado_label || 'Preço de tabela utilizado';
  }

  /** Corpo real verificado em pedido-adicionar-itens-modal.component.ts:5136-5163 (duplicado
   *  verbatim, mesmo padrão usado em `pedido-itens-detalhe-painel.component.ts`). */
  formatarValoresQuatroCasasDecimais(valor: number, porcentagem?: boolean, zero?: boolean): string {
    if (valor != null) {
      if (porcentagem) {
        return (valor).toLocaleString('pt-BR', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        });
      } else {
        return (valor).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        });
      }
    } else {
      if (zero) {
        return (0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        });
      }

      return null;
    }
  }
}
